// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Translate
import { TranslateService } from '@ngx-translate/core';
import { Cache, Auth } from 'aws-amplify';
import { AuthService } from '../../../../../app/core/auth';
import { MatSnackBar } from '@angular/material';
import { NgxPermissionsService } from 'ngx-permissions';
import { MixpanelService } from '../../../../../app/services/mixpanel.service';


/**
 * ! Just example => Should be removed in development
 */
const DEMO_PARAMS = {
	EMAIL: '',
	PASSWORD: ''
};

@Component({
	selector: 'kt-login',
	templateUrl: './login.component.html',
	encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {
	// Public params
	loginForm: FormGroup;
	loading = false;
	isLoggedIn$: Observable<boolean>;
	errors: any = [];

	private unsubscribe: Subject<any>;

	private returnUrl: any;

	// Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param router: Router
	 * @param auth: AuthService
	 * @param authNoticeService: AuthNoticeService
	 * @param translate: TranslateService
	 * @param store: Store<AppState>
	 * @param fb: FormBuilder
	 * @param cdr
	 * @param route
	 */
	constructor(
		private router: Router,
		private authService: AuthService,
		private translate: TranslateService,
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
		private route: ActivatedRoute,
		private _snackBar: MatSnackBar,
		private permissionsService: NgxPermissionsService,
		private mixpanelService: MixpanelService
	) {
		this.unsubscribe = new Subject();
	}


	ngOnInit(): void {
		this.initLoginForm();

		this.route.queryParams.subscribe(params => {
			this.returnUrl = params.returnUrl || '/';
		});
	}

	// onLogin() {
	// 	Cache.clear();
	// 	this.isLoading = true;
	// 	if ((this.email && this.email.length) && (this.password && (this.password.length > 7))) {
	// 		this.formErrMsg = null;

	// 	} else {
	// 		this.formErrMsg = 'Please provide valid Email and Password';
	// 	}
	// }

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.loading = false;
	}

	/**
	 * Form initalization
	 * Default params, validators
	 */
	initLoginForm() {

		this.loginForm = this.fb.group({
			email: [null, Validators.compose([
				Validators.required,
				Validators.email,
				Validators.minLength(3),
				Validators.maxLength(320) // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
			])
			],
			password: [null, Validators.compose([
				Validators.required,
				Validators.minLength(3),
				Validators.maxLength(100)
			])
			]
		});
	}

	submit() {
		const controls = this.loginForm.controls;
		/** check form */
		if (this.loginForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			return;
		}

		this.loading = true;

		const authData = {
			email: controls.email.value,
			password: controls.password.value
		};
		// console.log(authData);

		Auth.signIn(controls.email.value, controls.password.value)
			.then(user => {
				this.loading = false;
				// console.log(user);
				if (user.Session === null) {
					Cache.setItem('userAttr', user.attributes);
					this.authService.onUserAttrStored(true);

					// Use returnUrl if available and valid, otherwise default to dashboard
					const redirectUrl = this.isValidReturnUrl(this.returnUrl)
						? this.returnUrl
						: '/dashboard';
					this.router.navigateByUrl(redirectUrl);

					// let userRole = Cache.getItem('userAttr')['custom:role'];
					// console.log(userRole);
					// this.permissionsService.loadPermissions(userRole);
					// console.log('per:', this.permissionsService.getPermissions());
				} else {
					if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
						// /^(?=\D*\d)(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z]).{8,30}$/ //pwd pattern
						this.authService.setAuth({emailId: controls.email.value, password: controls.password.value});
						this.router.navigate(['new-password']);
					}
				}
				this.mixpanelService.setUserProperties(user.username, {
					'email': user.attributes.email,
					'firstName': user.attributes.given_name,
					'lastName': user.attributes.family_name,
					'role': user.attributes['custom:role'],
					'clientId': user.attributes['custom:client_id'],
					'distiId': user.attributes['custom:disti_id'],})
				this.mixpanelService.track(this.mixpanelService.LOGIN, {
					'user': user.username})
			})
			.catch(err => {
				this.loading = false;
				this.showSnackBar(err.message, 0);
			});


	}

	/**
	 * Validate returnUrl to prevent open redirect vulnerabilities
	 * Only allow internal routes (starting with / and not //)
	 */
	private isValidReturnUrl(url: string): boolean {
		if (!url || url === '/') {
			return false;
		}
		// Only allow internal routes (starting with /) and not external URLs (starting with //)
		return url.startsWith('/') && !url.startsWith('//');
	}

	/**
	 * Checking control validation
	 *
	 * @param controlName: string => Equals to formControlName
	 * @param validationType: string => Equals to valitors name
	 */
	isControlHasError(controlName: string, validationType: string): boolean {
		const control = this.loginForm.controls[controlName];
		if (!control) {
			return false;
		}

		const result = control.hasError(validationType) && (control.dirty || control.touched);
		return result;
	}

	showSnackBar(message: string, duration: number) {
		let snackRef = this._snackBar.open(message, 'close');
		if (duration > 0) {
			setTimeout(() => {
				this._snackBar.dismiss();
			}, duration);
		}

		snackRef.onAction().subscribe(() => {
			this._snackBar.dismiss();
		});
	}
}
